import { z } from 'zod';
// Shared between backend (DTO + use case) and admin (form + API client).
// Restaurant tariff config — Org Admin scoped (JQ4-16 / US-CONFIG-002).
// Hotel nightly tariff support added in JQ4-17 · US-CONFIG-003.
// Amounts are INTEGER CENTS. UI converts dollars<->cents at the form boundary.
export const TariffModeSchema = z.enum(['free', 'flat', 'hourly', 'nightly']);
const centsAmount = z.number().int().min(0).max(9_999_999);
// --- Per-mode schemas ---------------------------------------------------------
//
// Exported (event-tariff-override) — reused by UpsertEventTariffRequestSchema
// below, which builds the SAME discriminated union minus NightlySchema
// (events can never be priced per-night — ADR-5).
export const FreeSchema = z.object({
    mode: z.literal('free'),
    // free ignores differentiation entirely (calc always returns 0)
    differentiatedByDay: z.literal(false).default(false),
    amountCents: z.null().optional(),
    dailyCapCents: z.null().optional(),
    weekdayAmountCents: z.null().optional(),
    weekendAmountCents: z.null().optional(),
});
export const FlatSchema = z.object({
    mode: z.literal('flat'),
    differentiatedByDay: z.boolean().default(false),
    amountCents: centsAmount.nullable().optional(),
    weekdayAmountCents: centsAmount.nullable().optional(),
    weekendAmountCents: centsAmount.nullable().optional(),
    dailyCapCents: z.null().optional(), // cap is hourly-only
});
export const HourlySchema = z.object({
    mode: z.literal('hourly'),
    differentiatedByDay: z.boolean().default(false),
    amountCents: centsAmount.nullable().optional(),
    weekdayAmountCents: centsAmount.nullable().optional(),
    weekendAmountCents: centsAmount.nullable().optional(),
    dailyCapCents: centsAmount.nullable().optional(),
});
// Nightly mode — Hotel-niche locations only (JQ4-17 · US-CONFIG-003).
// amountCents, dailyCapCents, and weekday/weekend amounts are not applicable
// for nightly billing; use guestAmountCents / nonGuestAmountCents instead.
const NightlySchema = z.object({
    mode: z.literal('nightly'),
    differentiatedByDay: z.literal(false).default(false),
    guestAmountCents: centsAmount.nullable().optional(),
    nonGuestAmountCents: centsAmount.nullable().optional(),
    // Fields from other modes must be absent or null for nightly
    amountCents: z.null().optional(),
    dailyCapCents: z.null().optional(),
    weekdayAmountCents: z.null().optional(),
    weekendAmountCents: z.null().optional(),
});
function applyFlatHourlyInvariants(v, ctx) {
    if (v.differentiatedByDay) {
        // Required: weekday and weekend amounts must be present
        if (v.weekdayAmountCents == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['weekdayAmountCents'],
                message: 'Weekday amount is required when differentiatedByDay is true',
            });
        }
        if (v.weekendAmountCents == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['weekendAmountCents'],
                message: 'Weekend amount is required when differentiatedByDay is true',
            });
        }
        // Forbidden: base amountCents must not be present when differentiated
        if (v.amountCents != null) {
            ctx.addIssue({
                code: 'custom',
                path: ['amountCents'],
                message: 'amountCents must not be present when differentiatedByDay is true; use weekdayAmountCents and weekendAmountCents instead',
            });
        }
    }
    else {
        // Non-differentiated: base amount required
        if (v.amountCents == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['amountCents'],
                message: 'Amount is required when differentiatedByDay is false',
            });
        }
        // Forbidden: weekday/weekend amounts must not be present when non-differentiated
        if (v.weekdayAmountCents != null) {
            ctx.addIssue({
                code: 'custom',
                path: ['weekdayAmountCents'],
                message: 'weekdayAmountCents must not be present when differentiatedByDay is false',
            });
        }
        if (v.weekendAmountCents != null) {
            ctx.addIssue({
                code: 'custom',
                path: ['weekendAmountCents'],
                message: 'weekendAmountCents must not be present when differentiatedByDay is false',
            });
        }
    }
    // Cap must not be below the largest applicable rate (hourly only)
    if (v.mode === 'hourly' && v.dailyCapCents != null) {
        const rates = v.differentiatedByDay
            ? [v.weekdayAmountCents, v.weekendAmountCents]
            : [v.amountCents];
        const max = Math.max(...rates.filter((r) => r != null));
        if (Number.isFinite(max) && v.dailyCapCents < max) {
            ctx.addIssue({
                code: 'custom',
                path: ['dailyCapCents'],
                message: 'Daily cap cannot be lower than the hourly rate',
            });
        }
    }
}
// --- Location-only grace period (post-payment-delay-recharge · Slice S2) ----
//
// `graceMinutes` prices the customer-delay window for the delay-recharge
// feature: [readyForPickupAt + graceMinutes, now]. It applies to Flat/Hourly/
// Nightly location tariffs, NEVER to Free (nothing to delay-charge for a free
// location) and NEVER to event-tariff-overrides (events are NOT anchored by
// readyForPickupAt — out of scope, see design §File Changes S2). Extended
// ONLY onto the LOCATION variants below so the exported FreeSchema/FlatSchema/
// HourlySchema/NightlySchema used by UpsertEventTariffRequestSchema stay
// untouched.
//
// null means "use the system default (15 minutes)", resolved at COMPUTE time
// by TariffScheme.effectiveGraceMinutes — NOT defaulted here at storage time.
const graceMinutesField = z.number().int().min(0).max(1440).nullable().optional();
const LocationFreeSchema = FreeSchema.extend({
    graceMinutes: z.null().optional(),
});
const LocationFlatSchema = FlatSchema.extend({ graceMinutes: graceMinutesField });
const LocationHourlySchema = HourlySchema.extend({ graceMinutes: graceMinutesField });
const LocationNightlySchema = NightlySchema.extend({ graceMinutes: graceMinutesField });
// --- Discriminated union + cross-field invariants via superRefine ------------
export const UpsertTariffRequestSchema = z
    .discriminatedUnion('mode', [
    LocationFreeSchema,
    LocationFlatSchema,
    LocationHourlySchema,
    LocationNightlySchema,
])
    .superRefine((v, ctx) => {
    if (v.mode === 'free')
        return;
    // Nightly mode: both guest and non-guest amounts are required and >= 0
    if (v.mode === 'nightly') {
        if (v.guestAmountCents == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['guestAmountCents'],
                message: 'guestAmountCents is required for nightly mode',
            });
        }
        if (v.nonGuestAmountCents == null) {
            ctx.addIssue({
                code: 'custom',
                path: ['nonGuestAmountCents'],
                message: 'nonGuestAmountCents is required for nightly mode',
            });
        }
        return;
    }
    applyFlatHourlyInvariants(v, ctx);
});
// --- Event tariff request (event-tariff-override, Phase 5.1) -----------------
//
// SAME discriminated union + invariants as UpsertTariffRequestSchema, minus
// NightlySchema — an event can never be priced per-night (ADR-5), so nightly
// is rejected structurally (invalid_union_discriminator), not via a runtime
// check. Design §5 / spec Capability 1.
export const UpsertEventTariffRequestSchema = z
    .discriminatedUnion('mode', [FreeSchema, FlatSchema, HourlySchema])
    .superRefine((v, ctx) => {
    if (v.mode === 'free')
        return;
    applyFlatHourlyInvariants(v, ctx);
});
// --- Response DTO (returned by GET and PUT) ----------------------------------
export const TariffResponseSchema = z.object({
    id: z.string().uuid(),
    locationId: z.string().uuid(),
    organizationId: z.string().uuid(),
    mode: TariffModeSchema,
    amountCents: z.number().int().nullable(),
    dailyCapCents: z.number().int().nullable(),
    differentiatedByDay: z.boolean(),
    weekdayAmountCents: z.number().int().nullable(),
    weekendAmountCents: z.number().int().nullable(),
    // Hotel nightly rate fields (JQ4-17 · US-CONFIG-003). Present when mode=nightly, null otherwise.
    guestAmountCents: z.number().int().nullable().optional(),
    nonGuestAmountCents: z.number().int().nullable().optional(),
    // Delay-recharge grace period, in minutes (post-payment-delay-recharge · S2).
    // null = system default (15 min), resolved at compute time. Absent for events.
    graceMinutes: z.number().int().nullable(),
    effectiveFrom: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// --- Event tariff response DTO (event-tariff-override, Phase 5.1) -----------
//
// TariffResponseSchema minus guest/nonGuest (nightly-only) + effectiveFrom
// (location-only forward-scheduling seam; events are live-read only), with
// `eventId` instead of `locationId`. Design §5.
export const EventTariffResponseSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    organizationId: z.string().uuid(),
    mode: TariffModeSchema,
    amountCents: z.number().int().nullable(),
    dailyCapCents: z.number().int().nullable(),
    differentiatedByDay: z.boolean(),
    weekdayAmountCents: z.number().int().nullable(),
    weekendAmountCents: z.number().int().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=tariff-schemas.js.map